import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { TrendingUp, TrendingDown, Minus, Gauge } from "lucide-react";

/**
 * TrustScoreTrendCard — daily-snapshot history rendered as a tiny SVG
 * sparkline + delta callout ("+16 this month"). Loss-aversion bait that
 * gives providers a reason to log in every day and ask "what raises my
 * score?"
 *
 * Props:
 *   - providerId (string)
 *   - days (number, default 30) — sparkline window
 */
export default function TrustScoreTrendCard({ providerId, days = 30 }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!providerId) return;
    api.get(`/trust/score/${providerId}/history`, { params: { days } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [providerId, days]);

  if (!data || (data.points || []).length < 1) return null;

  const points = data.points;
  const delta = data.deltaWindow ?? 0;
  const current = data.current ?? 0;
  const showSpark = points.length >= 2;

  // Build SVG polyline coords; if there's only one point, draw a flat tick.
  const minS = Math.min(...points.map((p) => p.score));
  const maxS = Math.max(...points.map((p) => p.score));
  const range = Math.max(1, maxS - minS);
  const W = 280, H = 60, PAD = 4;
  const xs = (i) => PAD + (i * (W - 2 * PAD)) / Math.max(1, points.length - 1);
  const ys = (s) => H - PAD - ((s - minS) / range) * (H - 2 * PAD);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${xs(i).toFixed(1)} ${ys(p.score).toFixed(1)}`).join(" ");

  const deltaSign = delta > 0 ? "+" : "";
  const Trend = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const trendColor = delta > 0 ? "text-secondary" : delta < 0 ? "text-orange-300" : "text-foreground/55";

  return (
    <div className="border border-secondary/20 bg-card/40 rounded-sm p-4 space-y-3" data-testid="trust-score-trend">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-secondary/85">
          <Gauge size={14} strokeWidth={1.5} />
          <span className="label-mystic">Trust Score · last {data.windowDays} days</span>
        </div>
        <div className={`inline-flex items-center gap-1.5 ${trendColor} text-sm`} data-testid="trend-delta">
          <Trend size={14} strokeWidth={1.5} />
          <span className="font-serif text-base">{deltaSign}{delta}</span>
        </div>
      </div>

      <div className="flex items-end gap-4">
        <div className="leading-none">
          <span className="font-serif text-4xl text-foreground/95">{current}</span>
          <span className="text-foreground/45 text-xs ml-1">/ 100</span>
        </div>
        {showSpark && (
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="flex-1 max-w-full" data-testid="trend-spark">
            <defs>
              <linearGradient id="trustGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(212 175 55 / 0.4)" />
                <stop offset="100%" stopColor="rgb(212 175 55 / 0)" />
              </linearGradient>
            </defs>
            <path d={`${path} L ${xs(points.length - 1)} ${H - PAD} L ${PAD} ${H - PAD} Z`} fill="url(#trustGrad)" />
            <path d={path} fill="none" stroke="rgb(212 175 55)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
            {points.map((p, i) => (
              <circle key={i} cx={xs(i)} cy={ys(p.score)} r={1.5} fill="rgb(212 175 55)" />
            ))}
          </svg>
        )}
      </div>

      {delta !== 0 && (
        <p className="text-xs text-foreground/60">
          {delta > 0
            ? `You climbed ${delta} points in this window. Keep going — each verified review and endorsement compounds.`
            : `Your score dropped ${Math.abs(delta)} points. A new 5-star review or endorsement can lift it back fastest.`}
        </p>
      )}
      {delta === 0 && showSpark && (
        <p className="text-xs text-foreground/55">
          Steady at {current}. Adding lineage details or earning a peer endorsement is the fastest lift.
        </p>
      )}
    </div>
  );
}
